import { FormSelectInput } from '@tanium/react-form-select-input';
import { FormRow } from '@tanium/react-form';
import get from 'lodash.get';
import { Field, FieldProps, FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@tanium/react-theme-context';
import { Box } from '@tanium/react-box';
import { Stack } from '@tanium/react-stack';
import Tooltip from 'components/tooltip/Tooltip';
import { TARGET_ACCESS_ID } from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessType';
import { MsEntraIDGroupTargetAccess } from 'gql/MsEntraIDGroupTargetAccess';

interface IMsEntraIDGroupAccessProps {
  msEntraIDGroupTargetAccess: MsEntraIDGroupTargetAccess[];
}

export const MsEntraIDGroupAccess = ({
  msEntraIDGroupTargetAccess,
}: IMsEntraIDGroupAccessProps): React.ReactElement => {
  const theme = useTheme();
  const { t } = useTranslation();

  const formInputIdPrefix = 'create-request-';

  const options = msEntraIDGroupTargetAccess.map((access) => {
    return {
      label: access.roleName,
      value: access.id,
      description: access.description,
    };
  });

  return (
    <Box display="inline-flex" mr={1}>
      <Field name={TARGET_ACCESS_ID}>
        {({ field, form }: FieldProps): React.ReactElement => (
          <FormRow
            label={t('MS_ENTRA_ID_GROUP')}
            required
            description={
              <Box>
                <Box>{t('MS_ENTRA_ID_GROUP_DESCRIPTION')}</Box>
              </Box>
            }
            errors={(get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>}
            labelId={`${formInputIdPrefix}${field.name}`}
            data-testid={`${formInputIdPrefix}${field.name}`}
          >
            <FormSelectInput
              onChange={(option): void => {
                void form.setFieldValue(field.name, option.value);
              }}
              value={options?.find((option) => option.value === field.value)}
              inputId={`${formInputIdPrefix}${field.name}`}
              options={options}
              aria-label={t('MS_ENTRA_ID_GROUP')}
              formatOptionLabel={(
                option: {
                  value: string;
                  label: string;
                  description: string;
                },
                state
              ) => {
                const isSelected =
                  state.context === 'menu' && Array.isArray(state.selectValue) && option === state.selectValue[0];
                /* istanbul ignore next */
                const subtextColor = theme.colors[isSelected ? 'subdued' : 'subtle'];
                return (
                  <Stack direction="column" itemSpacing={0.5}>
                    <Box>{option.label}</Box>
                    <Tooltip content={option.description}>
                      <Box color={subtextColor} fontSize={11}>
                        {option.description}
                      </Box>
                    </Tooltip>
                  </Stack>
                );
              }}
              styles={{
                singleValue: (base) => ({
                  ...base,
                  marginTop: theme.space[1],
                  marginBottom: theme.space[1],
                  position: 'relative',
                  transform: 'none',
                  whiteSpace: 'normal',
                  maxHeight: '3.75em',
                }),
                input: (base) => ({
                  ...base,
                  position: 'absolute',
                  top: theme.space[1] - 2,
                  paddingTop: 0,
                }),
              }}
            />
          </FormRow>
        )}
      </Field>
    </Box>
  );
};

export default MsEntraIDGroupAccess;
